/**
 * Server-rendered head metadata per route.
 *
 * react-helmet-async only runs on the client, so on SSR every page used to ship
 * the root <title>. This module produces per-route <title>/<meta>/canonical that
 * TanStack Router emits directly into the server HTML.
 *
 * Curated entries win; everything else falls back to a humanized slug title.
 */

import valuationCopy from '@/data/translations/ro/valuation.json';
import { routeOgLocale } from './route-locale';
import { LANDING_PAGES } from '@/lib/landingPages';

const SITE = "RO Auto Market";
const ORIGIN = "https://roautomarket.ro";

export type RouteSeo = { title: string; description: string };

const CURATED: Record<string, RouteSeo> = {
  // Popular-search landing pages: fixed title/description from their config.
  ...Object.fromEntries(Object.values(LANDING_PAGES).map((p) => [`/${p.slug}`, { title: p.title, description: p.description }])),
  "/demo-gratuit": {
    title: "Demo gratuit | RO Auto Market",
    description: "Testează gratuit cum arată anunțul tău pe RO Auto Market. Fără cont, fără card, rezultat instant.",
  },
  "/cum-functioneaza": {
    title: "Cum funcționează | RO Auto Market",
    description: "Caută anunțuri, contactează vânzătorul și programează o vizionare. RO Auto Market verifică dealerii, nu vehiculele.",
  },
  "/uk/how-it-works": {
    title: "How it works | RO Auto Market UK",
    description: "Browse listings, contact sellers and arrange a viewing. RO Auto Market verifies dealers, not vehicles.",
  },
  "/de/how-it-works": {
    title: "Wie es funktioniert | RO Auto Market Deutschland",
    description: "Inserate ansehen, Verkäufer kontaktieren und eine Besichtigung vereinbaren. RO Auto Market überprüft Händler, nicht Fahrzeuge.",
  },
  "/": {
    title: "Marketplace auto România | Dealeri verificați | RO Auto Market",
    description: "Mașini second hand de la dealeri verificați ANAF și RISCO și vânzători privați. Suni direct vânzătorul și nu plătești comision.",
  },
  "/vehicles": {
    title: "Anunțuri auto: caută după marcă, preț și oraș",
    description:
      "Caută vehicule second hand după marcă, model, an, kilometraj, combustibil, preț și oraș.",
  },
  "/cars": {
    title: "Mașini de vânzare în România",
    description:
      "Anunțuri cu mașini de vânzare de la dealeri și persoane fizice. Filtrează după marcă, preț, an și kilometraj și contactează direct vânzătorul.",
  },
  "/masini-second-hand": {
    title: "Mașini pe mărci și orașe",
    description:
      "Alege marca sau localitatea și vezi câte anunțuri active sunt disponibile. Directorul RO Auto Market pe mărci și orașe din România.",
  },

  "/dealeri": {
    title: "Cont de dealer pe RO Auto Market | Prețuri și înscriere",
    description:
      "Creează cont de dealer pe RO Auto Market. Publică anunțuri, gestionează stocul și primește contacte. Prețuri de la 29,99 €/lună.",
  },
  "/dealeri-verificati": {
    title: "Dealeri auto verificați RISCO",
    description:
      "Lista dealerilor verificați pe RO Auto Market. Verificăm manual firma la ANAF și în RISCO. Nu verificăm starea vehiculelor.",
  },
  "/pricing": {
    title: "Prețuri și abonamente pentru dealeri și persoane fizice",
    description:
      "Planuri transparente: listare pentru persoane fizice și abonamente dealer Start, Standard și Large. Fără comision la vânzare.",
  },
  "/blog": {
    title: "Blog auto — ghiduri, știri și analize de piață",
    description:
      "Articole despre piața auto din România: ghiduri de cumpărare, costuri de întreținere, statistici și noutăți din industrie.",
  },
  "/contact": {
    title: "Contact",
    description:
      "Contactează echipa RO Auto Market pe email sau telefon pentru suport, colaborări și întrebări despre abonamente.",
  },
  "/despre-noi": {
    title: "Despre noi",
    description:
      "Află cine a construit RO Auto Market, cum sunt verificați dealerii prin RISCO și ANAF și cum poți contacta echipa.",
  },
  "/termeni-si-conditii": {
    title: "Termeni și condiții | RO Auto Market",
    description: "Citește regulile pentru conturi, anunțuri și plăți pe RO Auto Market, precum și responsabilitățile cumpărătorilor și vânzătorilor.",
  },
  "/terms": {
    title: "Termeni și condiții | RO Auto Market",
    description: "Citește regulile pentru conturi, anunțuri și plăți pe RO Auto Market, precum și responsabilitățile cumpărătorilor și vânzătorilor.",
  },
  "/masini-orase": {
    title: "Mașini de vânzare pe orașe din România",
    description:
      "Alege orașul și vezi mașinile disponibile local: București, Cluj-Napoca, Timișoara, Iași, Brașov, Constanța și restul țării.",
  },
  "/vinde-masina": {
    title: "Vinde-ți mașina online",
    description:
      "Publică anunțul în câteva minute: Standard 9,99 €, Promovat 14,99 € sau Activ până la vânzare 19,99 €/lună. Fără comision la vânzare.",
  },
  "/calculator-impozit-auto": {
    title: "Calculator impozit auto 2026",
    description:
      "Calculează impozitul auto anual în funcție de capacitatea cilindrică și norma de poluare, conform Codului Fiscal din România.",
  },
  "/evaluare-auto": {
    title: valuationCopy.title,
    description: valuationCopy.description,
  },

  "/ghiduri": {
    title: "Ghiduri auto pentru cumpărători și vânzători",
    description:
      "Ghiduri practice: verificarea istoricului, checklist la cumpărare, ITP, diferența dintre dealer verificat și vânzător privat.",
  },
  "/ghiduri/cum-vinzi-o-masina-in-romania": {
    title: "Cum vinzi o mașină în România: acte, contract, pași",
    description:
      "Pașii reali ai vânzării unei mașini: contractul ITL 054, transcrierea în 90 de zile, impozitul, RCA-ul și cum primești banii în siguranță.",
  },
  "/intrebari-frecvente": {
    title: "Întrebări frecvente",
    description:
      "Răspunsuri despre listarea anunțurilor, abonamentele dealer, verificarea dealerilor și siguranța tranzacțiilor.",
  },
  "/pentru-dealeri": {
    title: "Pentru dealeri auto — listează-ți stocul",
    description:
      "Pagină de dealer dedicată, import automat de stoc, lead-uri directe și statistici. Abonamente de la 29,99 €/lună.",
  },
  "/import": {
    title: "Import vehicule din Europa",
    description:
      "Servicii și informații despre importul de vehicule în România: costuri, acte necesare și pași până la înmatriculare.",
  },
  "/uk": {
    title: "Used cars for sale in the UK",
    description:
      "Browse used cars from verified dealers and private sellers, with full specs, photos and direct seller contact.",
  },
  "/de": {
    title: "Gebrauchtwagen kaufen",
    description:
      "Gebrauchtwagen von geprüften Händlern und Privatverkäufern — mit vollständigen Daten, Fotos und direktem Kontakt.",
  },
};

/** Routes that must never be indexed (private, admin, transactional). */
const NOINDEX_PREFIXES = [
  "/admin",
  "/statistici-auto-romania",
  "/dashboard",
  "/account",
  "/cont",
  "/auth",
  "/messages",
  "/favorites",
  "/my-alerts",
  "/my-listings-v2",
  "/edit-listing",
  "/edit-listing-v2",
  "/create-listing",
  "/create-listing-v2",
  "/create-dealer",
  "/activate-card",
  "/delete-account",
  "/success",
  "/seo-audit",
  "/gsc-integration",
  "/trap-",
  "/mockup-",
  "/.lovable",
  "/demo",
  "/listings-v2-demo",
  "/dealer-demo",
  "/bulk-import",
  "/import-stock",
  "/auto-statistics",
];

const LOCALE_PREFIXES = ["/uk", "/de", "/fr"];

/**
 * /cars is the main passenger-car page (server-rendered listings) and
 * /vehicles covers every category, so both are self-canonical. The
 * /masini-second-hand hub is a brand/city directory, not an equivalent list,
 * so nothing is canonicalized onto it.
 */
const CANONICAL_OVERRIDES: Record<string, string> = {};


function stripLocale(path: string): { locale: string; rest: string } {
  for (const p of LOCALE_PREFIXES) {
    if (path === p) return { locale: p, rest: "/" };
    if (path.startsWith(`${p}/`)) return { locale: p, rest: path.slice(p.length) };
  }
  return { locale: "", rest: path };
}

export function isNoindexPath(path: string): boolean {
  const { rest } = stripLocale(path);
  return NOINDEX_PREFIXES.some((p) =>
    p.endsWith('-') ? rest.startsWith(p) : rest === p || rest.startsWith(`${p}/`),
  );
}

const WORD_FIXES: Record<string, string> = {
  suv: "SUV",
  uk: "UK",
  bmw: "BMW",
  vw: "VW",
  itp: "ITP",
  gpl: "GPL",
  ulez: "ULEZ",
  mot: "MOT",
  euro: "Euro",
  seo: "SEO",
  faq: "Întrebări frecvente",
  v2: "",
};

const UUID_TAIL = /-[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

/** Brand slugs whose official spelling a generic humanizer gets wrong. */
const BRAND_DISPLAY: Record<string, string> = {
  "mercedes-benz": "Mercedes-Benz",
  "rolls-royce": "Rolls-Royce",
  "alfa-romeo": "Alfa Romeo",
  "land-rover": "Land Rover",
  mini: "MINI",
  ds: "DS",
  kia: "Kia",
};

export function humanizeSegment(segment: string): string {
  return segment
    .replace(UUID_TAIL, "")
    .split("-")
    .map((w) => WORD_FIXES[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1))
    .filter(Boolean)
    .join(" ")
    .trim();
}

function normalize(path: string): string {
  const trimmed = path.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/** Substitutes $params into a route pattern using actual values. */
function resolvePath(pattern: string, params?: Record<string, string | undefined>): string {
  return normalize(
    pattern.replace(/\$([A-Za-z0-9_]+)/g, (match, key: string) => {
      const value = params?.[key];
      return value ? String(value) : match;
    }),
  );
}

const CITY_PREFIXES: Array<{ prefix: string; build: (city: string) => RouteSeo }> = [
  {
    prefix: "masini-de-vanzare-",
    build: (city) => ({
      title: `Mașini de vânzare în ${city}`,
      description: `Anunțuri auto în ${city}: mașini second hand de la dealeri verificați și persoane fizice, cu preț, kilometraj și contact direct.`,
    }),
  },
  {
    prefix: "used-cars-",
    build: (city) => ({
      title: `Used cars for sale in ${city}`,
      description: `Used car listings in ${city} from verified dealers and private sellers, with price, mileage and direct seller contact.`,
    }),
  },
  {
    prefix: "gebrauchtwagen-",
    build: (city) => ({
      title: `Gebrauchtwagen in ${city}`,
      description: `Gebrauchtwagen-Angebote in ${city} von geprüften Händlern und Privatverkäufern — mit Preis, Kilometerstand und Direktkontakt.`,
    }),
  },
];

function fallbackSeo(resolved: string): RouteSeo {
  const { locale, rest } = stripLocale(resolved);
  const segments = rest.split("/").filter(Boolean);
  if (segments.length === 0) {
    return CURATED["/"];
  }
  if (segments.length === 1) {
    for (const { prefix, build } of CITY_PREFIXES) {
      if (segments[0].startsWith(prefix)) {
        return build(humanizeSegment(segments[0].slice(prefix.length)));
      }
    }
  }
  // Brand / model / fuel listing pages (/cars/volkswagen/golf-6, /de/cars/bmw, ...).
  // The generic label builder produced titles like "Cars — Volkswagen — Golf 6 în
  // România", which mixes languages and buries the search phrase. Build a natural
  // per-locale phrase instead so the model name leads the title.
  if (
    (segments[0] === "cars" || segments[0] === "masini-second-hand") &&
    segments.length >= 2 &&
    segments[1] !== "city" &&
    segments[1] !== "details"
  ) {
    const vehicle = segments
      .slice(1, 4)
      .map((s, i) => (i === 0 && BRAND_DISPLAY[s]) || humanizeSegment(s))
      .filter(Boolean)
      .join(" ");
    // Romanian brand-only page: stable title without counts/prices, and no
    // "second hand" because the selection also contains new vehicles.
    if (vehicle && locale === "" && segments[0] === "cars" && segments.length === 2) {
      return {
        title: `${vehicle} de vânzare`,
        description: `Anunțuri ${vehicle} de vânzare de la dealeri și persoane fizice, cu preț, an, kilometraj și fotografii. Contactează direct vânzătorul.`,
      };
    }
    if (vehicle) {
      if (locale === "/uk") {
        return {
          title: `Used ${vehicle} for sale`,
          description: `Used ${vehicle} listings on ${SITE} UK: price, mileage, year and full specs, from verified dealers and private sellers.`,
        };
      }
      if (locale === "/de") {
        return {
          title: `${vehicle} Gebrauchtwagen kaufen`,
          description: `${vehicle} Gebrauchtwagen auf ${SITE}: Preis, Kilometerstand, Baujahr und alle Daten, von geprüften Händlern und Privatverkäufern.`,
        };
      }
      if (locale === "/fr") {
        return {
          title: `${vehicle} d'occasion à vendre`,
          description: `Annonces ${vehicle} d'occasion sur ${SITE} : prix, kilométrage, année et fiche complète, de concessionnaires vérifiés et de particuliers.`,
        };
      }
      return {
        title: `${vehicle} second hand de vânzare`,
        description: `Anunțuri ${vehicle} second hand pe ${SITE}: preț, kilometraj, an de fabricație și dotări, de la dealeri verificați și persoane fizice.`,
      };
    }
  }
  // Keep the root segment in the label so sibling patterns that share the same
  // trailing slugs (/masini/$brand/$city vs /masini-second-hand/$brand/$city)
  // never produce an identical <title>, description or og:title.
  const parts =
    segments.length > 3
      ? [segments[0], ...segments.slice(-2)]
      : segments;
  const label = parts
    .map(humanizeSegment)
    .filter(Boolean)
    .join(" ");

  // Titles must stay unique per locale, otherwise /contact and /uk/contact
  // collide in search results with an identical <title> and og:title.
  if (locale === "/uk") {
    return {
      title: `${label} | UK`,
      description: `${label} on ${SITE} UK. See available listings and contact sellers directly.`,
    };
  }
  if (locale === "/de") {
    return {
      title: `${label} | Deutschland`,
      description: `${label} auf ${SITE} Deutschland. Anzeigen ansehen und Verkäufer direkt kontaktieren.`,
    };
  }
  if (locale === "/fr") {
    return {
      title: `${label} | France`,
      description: `${label} sur ${SITE} France. Consultez les annonces disponibles.`,
    };
  }
  return {
    title: `${label} în România`,
    description: `${label} pe ${SITE}. Vezi anunțurile disponibile și contactează direct vânzătorul.`,
  };
}

export function getRouteSeo(
  pattern: string,
  params?: Record<string, string | undefined>,
): RouteSeo & { canonical: string; hasUnresolvedParams: boolean } {
  const resolved = resolvePath(pattern, params);
  const seo = CURATED[normalize(pattern)] ?? CURATED[resolved] ?? fallbackSeo(resolved);
  const { locale } = stripLocale(resolved);
  // Only the Romanian versions collapse into one canonical; /uk/cars and
  // /de/autos are separate country hubs and keep their own canonical.
  const canonicalPath = (locale === "" && CANONICAL_OVERRIDES[resolved]) || resolved;
  return {
    ...seo,
    canonical: `${ORIGIN}${canonicalPath === "/" ? "/" : canonicalPath}`,
    hasUnresolvedParams: resolved.includes("$"),
  };

}

type HeadResult = {
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
};

/**
 * Builds route-level head metadata. Call from a route's `head()` option:
 *   head: ({ params }) => routeHead("/cars/$make", params)
 */
export function routeHead(
  pattern: string,
  params?: Record<string, string | undefined>,
): HeadResult {
  const { title, description, canonical, hasUnresolvedParams } = getRouteSeo(pattern, params);
  const fullTitle = title.includes(SITE) ? title : `${title} | ${SITE}`;
  const noindex = /^\/(de|uk)(\/|$)/.test(normalize(pattern)) || isNoindexPath(normalize(pattern));

  const meta: Array<Record<string, string>> = [
    { title: fullTitle },
    { name: "title", content: fullTitle },
    { name: "description", content: description },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
    { property: "og:locale", content: routeOgLocale(resolvePath(pattern, params)) },
  ];

  if (noindex) {
    meta.push({ name: "robots", content: "noindex, nofollow" });
  } else if (!hasUnresolvedParams) {
    meta.push({ property: "og:url", content: canonical });
  }

  const links: Array<Record<string, string>> =
    noindex || hasUnresolvedParams ? [] : [{ rel: "canonical", href: canonical }];

  return { meta, links };
}
